import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { TransmissionStatus, TransmissionState } from '@/mocks/adminDashboard';

interface TransmissionStatusCardProps {
  transmission: TransmissionStatus;
}

interface StateVisual {
  label: string;
  icon: string;
  gradientFrom: string;
  gradientTo: string;
  dotColor: string;
  textColor: string;
  badgeBg: string;
  badgeText: string;
  pulse: boolean;
}

const stateVisuals: Record<TransmissionState, StateVisual> = {
  transmitting: {
    label: 'Transmitiendo',
    icon: 'ri-broadcast-line',
    gradientFrom: 'from-emerald-50',
    gradientTo: 'to-emerald-50/30',
    dotColor: 'bg-emerald-500',
    textColor: 'text-emerald-600',
    badgeBg: 'bg-emerald-500/10',
    badgeText: 'text-emerald-700',
    pulse: true,
  },
  autodj: {
    label: 'AutoDJ',
    icon: 'ri-play-list-2-line',
    gradientFrom: 'from-amber-50',
    gradientTo: 'to-amber-50/30',
    dotColor: 'bg-amber-500',
    textColor: 'text-amber-600',
    badgeBg: 'bg-amber-500/10',
    badgeText: 'text-amber-700',
    pulse: false,
  },
  scheduled: {
    label: 'Programado',
    icon: 'ri-calendar-check-line',
    gradientFrom: 'from-sky-50',
    gradientTo: 'to-sky-50/30',
    dotColor: 'bg-sky-500',
    textColor: 'text-sky-600',
    badgeBg: 'bg-sky-500/10',
    badgeText: 'text-sky-700',
    pulse: false,
  },
  offline: {
    label: 'Fuera del Aire',
    icon: 'ri-cloud-off-line',
    gradientFrom: 'from-background-100',
    gradientTo: 'to-background-50',
    dotColor: 'bg-foreground-400',
    textColor: 'text-foreground-500',
    badgeBg: 'bg-foreground-100',
    badgeText: 'text-foreground-600',
    pulse: false,
  },
  external_streaming: {
    label: 'Usando Streaming Externo',
    icon: 'ri-cloud-line',
    gradientFrom: 'from-secondary-50',
    gradientTo: 'to-secondary-50/30',
    dotColor: 'bg-secondary-500',
    textColor: 'text-secondary-600',
    badgeBg: 'bg-secondary-500/10',
    badgeText: 'text-secondary-700',
    pulse: false,
  },
  rfw_streaming: {
    label: 'Usando Streaming RFW',
    icon: 'ri-signal-tower-line',
    gradientFrom: 'from-accent-50',
    gradientTo: 'to-accent-50/30',
    dotColor: 'bg-accent-500',
    textColor: 'text-accent-600',
    badgeBg: 'bg-accent-500/10',
    badgeText: 'text-accent-700',
    pulse: false,
  },
  unconfigured: {
    label: 'Sin configurar',
    icon: 'ri-settings-3-line',
    gradientFrom: 'from-background-100',
    gradientTo: 'to-background-50',
    dotColor: 'bg-foreground-300',
    textColor: 'text-foreground-400',
    badgeBg: 'bg-foreground-100',
    badgeText: 'text-foreground-500',
    pulse: false,
  },
};

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Ahora mismo';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Hace ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `Hace ${diffD}d`;
}

function formatScheduledTime(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const time = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === today.toDateString()) return `Hoy a las ${time}`;
  if (date.toDateString() === tomorrow.toDateString()) return `Mañana a las ${time}`;
  return `${date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })} a las ${time}`;
}

function getStreamTypeLabel(streamType: 'external' | 'rfw' | 'none', state: TransmissionState): string | null {
  if (state === 'external_streaming') return 'Señal desde tu propio servidor';
  if (state === 'rfw_streaming') return 'Señal alojada en RFW';
  if (state === 'transmitting' || state === 'autodj') {
    if (streamType === 'external') return 'Señal desde tu propio servidor';
    if (streamType === 'rfw') return 'Señal alojada en RFW';
  }
  return null;
}

export default function TransmissionStatusCard({ transmission }: TransmissionStatusCardProps) {
  const [animateIn, setAnimateIn] = useState(false);
  const visual = stateVisuals[transmission.state];

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const isActive = transmission.state === 'transmitting' || transmission.state === 'autodj';
  const isScheduled = transmission.state === 'scheduled';
  const isOffline = transmission.state === 'offline';
  const isUnconfigured = transmission.state === 'unconfigured';
  const needsSetup = isOffline || isUnconfigured;
  const streamLabel = getStreamTypeLabel(transmission.streamType, transmission.state);

  return (
    <div
      className={`bg-background-50 border border-background-200 rounded-2xl overflow-hidden transition-all duration-500 ease-out ${
        animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* ── Header bar ── */}
      <div className={`bg-gradient-to-r ${visual.gradientFrom} ${visual.gradientTo} px-5 py-4 border-b border-background-200`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${needsSetup ? 'bg-background-200' : 'bg-white'} flex items-center justify-center`}>
              <i className={`${visual.icon} ${visual.textColor} text-lg`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-foreground-950 font-bold text-sm">Estado de Transmisión</h3>
                {visual.pulse && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 text-[10px] font-bold">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                    EN VIVO
                  </span>
                )}
              </div>
              <p className={`text-xs font-semibold mt-0.5 ${visual.textColor}`}>
                {visual.label}
              </p>
            </div>
          </div>
          {streamLabel && (
            <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${visual.badgeBg} ${visual.badgeText}`}>
              {streamLabel}
            </span>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="px-5 py-4">
        {needsSetup ? (
          /* ── No transmission: friendly recommendation ── */
          <div className="text-center py-3">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className={`${visual.icon} text-2xl ${visual.textColor}`}></i>
            </div>
            <h4 className="text-foreground-800 font-bold text-sm mb-1">
              {isUnconfigured ? 'Empezá a transmitir' : 'Tu radio está en silencio'}
            </h4>
            <p className="text-foreground-400 text-xs max-w-sm mx-auto mb-4 leading-relaxed">
              {isUnconfigured
                ? 'Todavía no configuraste tu señal de audio. Es muy fácil — conectá tu servidor o empezá con unos pocos clics.'
                : 'Tu señal no está activa en este momento. Tus oyentes te extrañan — ¡volvé al aire en minutos!'}
            </p>
            <Link
              to="/admin/broadcast-center"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-broadcast-line text-sm"></i>
              {isUnconfigured ? 'Configurar transmisión' : 'Ir al centro de transmisión'}
            </Link>
          </div>
        ) : isScheduled ? (
          /* ── Scheduled ── */
          <div className="flex flex-col items-center py-3">
            <div className="w-14 h-14 rounded-2xl bg-sky-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-time-line text-2xl text-sky-500"></i>
            </div>
            <h4 className="text-foreground-800 font-bold text-sm mb-1">
              {transmission.currentProgram || 'Transmisión programada'}
            </h4>
            <p className="text-foreground-400 text-xs mb-3">
              Comienza {formatScheduledTime(transmission.scheduledFor || transmission.startedAt)}
            </p>
            {transmission.currentHost && (
              <div className="flex items-center gap-2 text-foreground-600 text-xs">
                <i className="ri-user-voice-line text-xs text-sky-500"></i>
                <span>{transmission.currentHost}</span>
              </div>
            )}
            {transmission.nextProgram && (
              <p className="text-foreground-400 text-[10px] mt-2">
                Después: {transmission.nextProgram}
              </p>
            )}
          </div>
        ) : (
          /* ── Active transmission ── */
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Current program */}
            <div className="bg-background-100 rounded-xl p-3 col-span-2 sm:col-span-2">
              <span className="text-foreground-400 text-[10px] font-medium block mb-1">Programa actual</span>
              <p className="text-foreground-950 text-sm font-bold">{transmission.currentProgram || 'Sin programa'}</p>
              {transmission.nextProgram && (
                <p className="text-foreground-400 text-[10px] mt-0.5">Luego: {transmission.nextProgram}</p>
              )}
            </div>

            {/* Host */}
            <div className="bg-background-100 rounded-xl p-3">
              <span className="text-foreground-400 text-[10px] font-medium block mb-1">Al aire</span>
              <p className="text-foreground-950 text-sm font-bold flex items-center gap-1.5">
                <i className="ri-user-voice-line text-xs text-accent-500"></i>
                {transmission.currentHost || 'AutoDJ'}
              </p>
            </div>

            {/* Listeners */}
            <div className="bg-background-100 rounded-xl p-3">
              <span className="text-foreground-400 text-[10px] font-medium block mb-1">Oyentes</span>
              <p className="text-foreground-950 text-sm font-bold flex items-center gap-1.5">
                <i className="ri-headphone-line text-xs text-accent-500"></i>
                {transmission.listeners.toLocaleString()}
              </p>
            </div>

            {/* Duration */}
            <div className="bg-background-100 rounded-xl p-3 col-span-2">
              <span className="text-foreground-400 text-[10px] font-medium block mb-1">En el aire desde</span>
              <p className="text-foreground-800 text-xs font-semibold flex items-center gap-1.5">
                <i className="ri-timer-line text-xs text-foreground-400"></i>
                {formatTimeAgo(transmission.startedAt)}
                <span className="text-foreground-400 font-normal">· {new Date(transmission.startedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
              </p>
            </div>

            {/* Quick actions */}
            <div className="col-span-2 flex items-center gap-2">
              <Link
                to="/admin/broadcast-center"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-broadcast-line text-xs"></i>
                Gestionar transmisión
              </Link>
              <Link
                to="/admin/schedule"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-2-line text-xs"></i>
                Programación
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}